import { useState, type ReactNode } from 'react'
import { api } from '@/api/client'
import { CoachPanel } from '@/components/CoachPanel'
import { AuthoringReview } from '@/components/AuthoringReview'
import { Icon, type IconName } from '@/components/Icon'
import { describeTaskEvent } from '@/components/Timeline'
import { useAsync } from '@/components/useAsync'
import { fmtDate } from '@/lib/format'
import { PersonName } from '@/components/PersonName'
import type { People, Role, RubricReview, Task, TaskEvent } from '@/lib/types'
import { ReviewPanel } from './ReviewPanel'
import type { ReviewHistory } from './types'
import { coachReviewStatus } from './labels'

interface Activity {
  id: string
  at: string
  title: string
  icon: IconName
  actor?: ReactNode
  status?: string
  message?: string
  detail?: ReactNode
  action?: string
}
const reviewStatus: Record<string, string> = {
  open: 'In review', deciding: 'Decision in progress', approved: 'Approved', returned: 'Changes requested',
  publishing: 'Publishing', published: 'Published', superseded: 'Replaced by a newer review',
}
/** One history for workflow events and reviews, including records created before this UI. */
export function TaskActivity({ task, events, role, authoringReviews = [], people }: {
  task: Task; events: TaskEvent[]; role: Role; authoringReviews?: RubricReview[]; people?: People
}) {
  const { data, error, reload } = useAsync(
    () => api.get<ReviewHistory>(`/tasks/${task.task_id}/reviews`), [task.task_id, task.revision],
  )
  const items: Activity[] = events.map((event, index) => ({
    id: `event-${event.at}-${index}`, at: event.at, title: describeTaskEvent(event), icon: 'clock',
    actor: event.actor_email && <PersonName people={people} id={event.actor_id} email={event.actor_email} />,
    message: ['transition', 'peer_reviewed', 'coach_review_waived', 'task_retired', 'authoring_resumed'].includes(event.type) &&
      event.message !== 'Task moved to the next stage.' ? event.message : undefined,
  }))
  const questions = authoringReviews.filter(review => review.kind === 'authoring_blocker')
  if (questions.length) {
    const latest = questions.reduce((a, b) => a.requested_at > b.requested_at ? a : b)
    items.push({ id: 'authoring-questions', at: latest.requested_at, title: 'Authoring questions', icon: 'chat',
      actor: 'Task authoring agent', action: 'View questions and replies',
      detail: <AuthoringReview data={{ task, reviews: questions, people }} role={role} readOnly />,
    })
  }
  for (const review of data?.coach_reviews ?? []) {
    items.push({
      id: `coach-${review.review_id}`, at: review.created_at, title: 'Draft coach review', icon: 'chat',
      actor: <PersonName people={people} id={task.sme_id} email={task.sme_email} />, status: coachReviewStatus[review.status] ?? review.status,
      action: 'View conversation',
      detail: <CoachPanel taskId={task.task_id} role={role} compact readOnly
        conversationUrl={`/tasks/${task.task_id}/conversations/draft-${review.review_id}`} />,
    })
  }
  for (const round of data?.rounds ?? []) {
    items.push({
      id: `round-${round.round_id}`, at: round.created_at,
      title: round.kind === 'quality' ? 'Quality review' : 'Peer review', icon: round.kind === 'quality' ? 'check' : 'people',
      status: round.closed_reason === 'task_retired' ? 'Closed when task retired' : reviewStatus[round.status] ?? round.status,
      action: 'View review', detail: <ReviewPanel taskId={task.task_id} roundId={round.round_id} role={role} readOnly={task.lifecycle === 'retired'} />,
    })
  }
  items.sort((a, b) => b.at.localeCompare(a.at))
  return <section className="task-activity" aria-label="Activity history">
    <h2>Activity</h2>
    {error && <p className="note err" role="alert">Could not load reviews. <button className="link small" onClick={reload}>Retry</button></p>}
    {!data && !error && <p className="muted small" role="status">Loading reviews…</p>}
    {data && !items.length && <p className="muted small">No activity yet.</p>}
    <ol className="activity-list">
      {items.map(item => <ActivityRow key={item.id} item={item} />)}
    </ol>
  </section>
}

function ActivityRow({ item }: { item: Activity }) {
  const [open, setOpen] = useState(false)
  return <li className="activity-item">
    <span className="activity-icon"><Icon name={item.icon} size={18} /></span>
    <div className="activity-content">
      <div className="activity-title"><strong>{item.title}</strong>{item.status && <span className="muted small">{item.status}</span>}</div>
      <div className="activity-meta muted small"><time dateTime={item.at}>{fmtDate(item.at)}</time>{item.actor && <span>{item.actor}</span>}</div>
      {item.message && <p className="prewrap activity-message">{item.message}</p>}
      {item.detail && <details className="activity-detail" onToggle={e => setOpen(e.currentTarget.open)}>
        <summary>{item.action}</summary>
        {open && <div className="activity-expanded">{item.detail}</div>}
      </details>}
    </div>
  </li>
}
